import classNames from 'classnames'
import React, { useRef, useEffect, useState } from 'react'
import { Hint } from '../search-input/react-autcomplete-hint'
import useUniqueId from '../hooks/use-unique-id'
import SearchIcon from '../icon/generated/search'
import CloseIcon from '../icon/generated/close'
import { useTopLoader } from 'nextjs-toploader'

type Props = {
  label: string
  onChange: (arg0: string) => void
  onSubmit?: (arg0: string) => void
  setSearchInputFocus?: (arg0: boolean) => void
  withSearchBtn?: boolean
  withDeleteBtn?: boolean
  placeholder?: string
  id?: string
  className?: string
  inputClassName?: string
  visibleLabel?: boolean
  suggestion?: string | null
  triggerInputFocus?: boolean
  isLoading?: boolean
  ['data-test-id']?: string
}

export const SearchInput = ({
  label = '',
  onChange,
  onSubmit,
  setSearchInputFocus,
  withSearchBtn = true,
  withDeleteBtn = false,
  placeholder = '',
  id,
  className = '',
  inputClassName = '',
  visibleLabel = false,
  suggestion,
  triggerInputFocus = false,
  isLoading = false,
  ...props
}: Props) => {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const uniqueId = useUniqueId('search-input')
  const inputId = id ?? uniqueId
  const [disableHints, setDisableHints] = useState(false)
  const topLoader = useTopLoader()

  useEffect(() => {
    if (triggerInputFocus) {
      focusInput()
    }
  }, [triggerInputFocus])

  useEffect(() => {
    if (isLoading) {
      setDisableHints(true)
      return
    }

    setDisableHints(false)
  }, [isLoading])

  const focusInput = () => {
    const inputValue = searchInputRef.current?.value || ''

    searchInputRef.current?.focus()
    searchInputRef.current?.setSelectionRange(inputValue.length, inputValue.length)
  }

  const handleDeleteBtn = () => {
    searchInputRef.current!.value = ''
    onChange('')
  }

  const handleSubmit = () => {
    setDisableHints(true)
    const inputValue = searchInputRef.current?.value

    if (inputValue) {
      onSubmit && onSubmit(inputValue)
      topLoader.start()
    }
  }

  const hintOptions = suggestion ? [suggestion] : []

  return (
    <form className={`c-search-input-new ${className}`} autoComplete="off">
      <label
        htmlFor="search-input"
        className={classNames({
          'u-sr-only': !visibleLabel,
        })}>
        {label}
      </label>
      <div
        className={classNames('c-search-input-new__wrap u-radius-2', {
          // 'u-mb-4': isSmallScreen
        })}>
        <Hint
          mainInputRef={searchInputRef}
          options={hintOptions}
          disableHint={disableHints}
          allowTabFill>
          <input
            id={inputId}
            type="search"
            data-test-id={props['data-test-id']}
            className={classNames('c-search-input-new__input', inputClassName, {
              'u-pr-96': withDeleteBtn,
              'u-pr-56': withSearchBtn,
            })}
            placeholder={placeholder}
            onChange={e => {
              onChange(e.target.value)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSubmit()
              } else if (e.key === 'Backspace') {
                setDisableHints(true)
                return
              }

              if (e.key !== ('Escape' || 'Backspace')) {
                setSearchInputFocus && setSearchInputFocus(true)
              }

              setDisableHints(false)
            }}
            autoComplete="off"
          />
        </Hint>
        <div className="c-search-input-new__btn-wrap">
          {withDeleteBtn && (
            <button
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault()
              }}
              type="button"
              className="c-search-input-new__btn-delete"
              aria-label="close search"
              onClick={handleDeleteBtn}>
              <CloseIcon name="close" className="i-16" />
            </button>
          )}
          {withSearchBtn && (
            <button
              type="button"
              name="search"
              className={classNames('c-search-input-new__btn-search', {
                'u-disabled': !onSubmit,
              })}
              aria-label="search"
              onClick={handleSubmit}>
              <SearchIcon name="search" className="i-16" />
            </button>
          )}
        </div>
      </div>
    </form>
  )
}

export default SearchInput
